import React from 'react';
import { TrendingUp, DollarSign, Percent, BarChart, PieChart, ChevronUp, ChevronDown, Activity, AlertCircle, CircleDollarSign, Scale } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета рентабельности
 */
const ResultsOverview = ({ results, formData, industryTypes }) => {
  if (!results) return null;

  // Получаем данные по отрасли для сравнения
  const industry = industryTypes.find(ind => ind.id === formData.industryType) || {
    name: 'Не указана',
    avgGrossMargin: 0,
    avgNetMargin: 0,
    avgROI: 0
  };

  // Функция для определения класса отклонения от среднего по отрасли
  const getComparisonClass = (actual, benchmark, isHigherBetter = true) => {
    if (Math.abs(actual - benchmark) <= 1) return 'text-gray-600 dark:text-gray-400'; // Примерно равно
    return isHigherBetter 
      ? (actual > benchmark ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')
      : (actual < benchmark ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400');
  };

  // Функция для получения стрелки индикатора сравнения
  const getComparisonIcon = (actual, benchmark, isHigherBetter = true) => {
    if (Math.abs(actual - benchmark) <= 1) return null; // Примерно равно

    const isPositive = isHigherBetter ? actual > benchmark : actual < benchmark;
    
    return isPositive 
      ? <ChevronUp className="w-4 h-4 text-emerald-500" />
      : <ChevronDown className="w-4 h-4 text-red-500" />;
  };

  return (
    <ToolResult title="Результаты расчета рентабельности">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Валовая маржа */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Валовая маржа
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatNumber(results.grossProfitMargin, 1)}%
            </span>
            <div className="flex items-center text-sm">
              {getComparisonIcon(results.grossProfitMargin, industry.avgGrossMargin)}
              <span className={`ml-1 ${getComparisonClass(results.grossProfitMargin, industry.avgGrossMargin)}`}>
                Ср. по отрасли: {formatNumber(industry.avgGrossMargin, 1)}%
              </span>
            </div>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Отношение валовой прибыли к выручке
          </p>
        </div>

        {/* Чистая маржа */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Чистая маржа
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.netProfitMargin, 1)}%
            </span>
            <div className="flex items-center text-sm">
              {getComparisonIcon(results.netProfitMargin, industry.avgNetMargin)}
              <span className={`ml-1 ${getComparisonClass(results.netProfitMargin, industry.avgNetMargin)}`}>
                Ср. по отрасли: {formatNumber(industry.avgNetMargin, 1)}%
              </span>
            </div>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Отношение чистой прибыли к выручке
          </p>
        </div>

        {/* Рентабельность инвестиций (ROI) */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            ROI
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
              {formatNumber(results.roi, 1)}%
            </span>
            <div className="flex items-center text-sm">
              {getComparisonIcon(results.roi, industry.avgROI)}
              <span className={`ml-1 ${getComparisonClass(results.roi, industry.avgROI)}`}>
                Ср. по отрасли: {formatNumber(industry.avgROI, 1)}%
              </span>
            </div>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            Отношение чистой прибыли к инвестициям
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <BarChart className="w-5 h-5 text-emerald-500" />
          Абсолютные показатели
        </h4>
        
        <ResultItem 
          label="Выручка" 
          value={formatCurrency(results.revenue)} 
          color="emerald" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Валовая прибыль" 
          value={formatCurrency(results.grossProfit)} 
          color="emerald" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Операционная прибыль (EBIT)" 
          value={formatCurrency(results.operatingProfit)} 
          color={results.operatingProfit >= 0 ? "emerald" : "red"} 
          icon={<DollarSign className="w-4 h-4"/>} 
        />
        
        {formData.includeAdditionalMetrics && (
          <ResultItem 
            label="EBITDA" 
            value={formatCurrency(results.ebitda)} 
            color={results.ebitda >= 0 ? "emerald" : "red"} 
            icon={<DollarSign className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Чистая прибыль" 
          value={formatCurrency(results.netProfit)} 
          color={results.netProfit >= 0 ? "emerald" : "red"}
          icon={<DollarSign className="w-4 h-4"/>} 
        />

        <div className="h-0.5 bg-gray-100 dark:bg-gray-700 my-4"></div>

        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <PieChart className="w-5 h-5 text-blue-500" />
          Относительные показатели
        </h4>
        
        <ResultItem 
          label="Валовая маржа" 
          value={`${formatNumber(results.grossProfitMargin, 2)}%`} 
          color="emerald" 
          icon={<Percent className="w-4 h-4"/>}
          comparison={`Ср. по отрасли: ${formatNumber(industry.avgGrossMargin, 1)}%`}
          comparisonColor={getComparisonClass(results.grossProfitMargin, industry.avgGrossMargin)}
        />
        
        <ResultItem 
          label="Операционная маржа" 
          value={`${formatNumber(results.operatingMargin, 2)}%`} 
          color={results.operatingMargin >= 0 ? "emerald" : "red"} 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Чистая маржа" 
          value={`${formatNumber(results.netProfitMargin, 2)}%`} 
          color={results.netProfitMargin >= 0 ? "emerald" : "red"}
          icon={<Percent className="w-4 h-4"/>}
          comparison={`Ср. по отрасли: ${formatNumber(industry.avgNetMargin, 1)}%`}
          comparisonColor={getComparisonClass(results.netProfitMargin, industry.avgNetMargin)}
        />
        
        <ResultItem 
          label="Рентабельность инвестиций (ROI)" 
          value={`${formatNumber(results.roi, 2)}%`} 
          color={results.roi >= 0 ? "emerald" : "red"} 
          icon={<TrendingUp className="w-4 h-4"/>}
          comparison={`Ср. по отрасли: ${formatNumber(industry.avgROI, 1)}%`}
          comparisonColor={getComparisonClass(results.roi, industry.avgROI)}
        />
        
        {formData.includeAdditionalMetrics && (
          <>
            <ResultItem 
              label="Рентабельность активов (ROA)" 
              value={`${formatNumber(results.roa, 2)}%`} 
              color={results.roa >= 0 ? "emerald" : "red"} 
              icon={<TrendingUp className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Рентабельность собственного капитала (ROE)" 
              value={`${formatNumber(results.roe, 2)}%`} 
              color={results.roe >= 0 ? "emerald" : "red"} 
              icon={<TrendingUp className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Рентабельность по EBITDA" 
              value={`${formatNumber(results.ebitdaMargin, 2)}%`} 
              color={results.ebitdaMargin >= 0 ? "emerald" : "red"} 
              icon={<Percent className="w-4 h-4"/>}
            />
          </>
        )}

        {formData.includeAdditionalMetrics && (
          <>
            <div className="h-0.5 bg-gray-100 dark:bg-gray-700 my-4"></div>

            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-500" />
              Дополнительные показатели
            </h4>
            
            <ResultItem 
              label="Точка безубыточности" 
              value={formatCurrency(results.breakEvenPoint)} 
              color="blue" 
              icon={<Scale className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Запас финансовой прочности" 
              value={`${formatNumber(results.financialSafety, 2)}%`} 
              color={results.financialSafety >= 15 ? "emerald" : results.financialSafety >= 5 ? "yellow" : "red"} 
              icon={<Percent className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Операционный рычаг" 
              value={formatNumber(results.operatingLeverage, 2)} 
              color="blue" 
              icon={<Scale className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Отношение долга к EBITDA" 
              value={formatNumber(results.debtToEbitda, 2)} 
              color={results.debtToEbitda <= 3 ? "emerald" : results.debtToEbitda <= 5 ? "yellow" : "red"} 
              icon={<CircleDollarSign className="w-4 h-4"/>}
            />
          </>
        )}

        {/* Экспертная оценка результатов */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Экспертная оценка</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {results.netProfitMargin >= industry.avgNetMargin 
                  ? `Показатели рентабельности бизнеса ${results.netProfitMargin > industry.avgNetMargin + 5 ? 'значительно превышают' : 'превышают'} средние значения в отрасли "${industry.name}".`
                  : `Показатели рентабельности бизнеса ${industry.avgNetMargin - results.netProfitMargin > 5 ? 'значительно ниже' : 'ниже'} средних значений в отрасли "${industry.name}".`
                }
                {results.netProfitMargin <= 0 
                  ? ' Бизнес убыточен, требуются срочные меры по оптимизации расходов и увеличению выручки.'
                  : results.netProfitMargin < industry.avgNetMargin / 2
                    ? ' Рекомендуется анализ структуры затрат и поиск путей повышения эффективности.'
                    : results.netProfitMargin >= industry.avgNetMargin * 1.5
                      ? ' Это указывает на высокую эффективность бизнес-модели и конкурентное преимущество.'
                      : ' Рентабельность находится в пределах нормы для данного типа бизнеса.'
                }
              </p>
              {results.financialSafety < 10 && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  Внимание: низкий запас финансовой прочности ({formatNumber(results.financialSafety, 1)}%) свидетельствует о повышенных рисках. 
                  Рекомендуется увеличить маржинальность или снизить постоянные расходы.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;